import React, { useEffect, useRef, useState } from 'react';

// Configuration
const SIGNALING_SERVER_URL = "ws://startup4.ipserlab.com:8082/socket";
const ICE_SERVERS = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };
const DEFAULT_ROOM_ID = "demo-room-1";

const VideoRoom = () => {
    const [status, setStatus] = useState("Disconnected");
    const [peerReady, setPeerReady] = useState(false);
    const [isCallActive, setIsCallActive] = useState(false);

    // All mutable state lives in refs to avoid re-render cascades
    const ws = useRef(null);
    const pc = useRef(null);
    const localStream = useRef(null);
    const targetUserIdRef = useRef(null);
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);

    // --- Helpers (use refs, no dependencies) ---

    const wsSend = (type, payload = {}) => {
        if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type, ...payload }));
        }
    };

    const sendSignaling = (data) => {
        const target = targetUserIdRef.current;
        if (!target) return;
        wsSend('SIGNALING_MESSAGE', { targetUserId: target, data });
    };

    const createPeerConnection = () => {
        if (pc.current) return pc.current;

        const _pc = new RTCPeerConnection(ICE_SERVERS);

        _pc.onicecandidate = (e) => {
            if (e.candidate) sendSignaling({ type: 'candidate', candidate: e.candidate });
        };

        _pc.ontrack = (e) => {
            if (remoteVideoRef.current) remoteVideoRef.current.srcObject = e.streams[0];
            setIsCallActive(true);
        };

        // Detect when remote peer disconnects
        _pc.onconnectionstatechange = () => {
            console.log('Connection state:', _pc.connectionState);
            if (_pc.connectionState === 'disconnected' ||
                _pc.connectionState === 'failed' ||
                _pc.connectionState === 'closed') {
                clearRemoteVideo();
            }
        };

        _pc.oniceconnectionstatechange = () => {
            console.log('ICE connection state:', _pc.iceConnectionState);
            if (_pc.iceConnectionState === 'disconnected' ||
                _pc.iceConnectionState === 'failed' ||
                _pc.iceConnectionState === 'closed') {
                clearRemoteVideo();
            }
        };

        // Attach local tracks if media is already acquired
        if (localStream.current) {
            localStream.current.getTracks().forEach(t => _pc.addTrack(t, localStream.current));
        }

        pc.current = _pc;
        return _pc;
    };

    const handleSignaling = async (data) => {
        const _pc = createPeerConnection();
        try {
            if (data.type === 'offer') {
                // Reject offers when already in an active call (prevents 3rd-party corruption)
                if (pc.current && pc.current.connectionState === 'connected') {
                    console.warn('Rejected incoming offer — already in an active call');
                    return;
                }

                // Answering side: acquire media first so tracks are sent back
                if (!localStream.current) await acquireMedia();
                if (localStream.current) {
                    localStream.current.getTracks().forEach(t => {
                        if (!_pc.getSenders().find(s => s.track === t)) {
                            _pc.addTrack(t, localStream.current);
                        }
                    });
                }
                await _pc.setRemoteDescription(new RTCSessionDescription(data));
                const answer = await _pc.createAnswer();
                await _pc.setLocalDescription(answer);
                sendSignaling(answer);
            } else if (data.type === 'answer') {
                await _pc.setRemoteDescription(new RTCSessionDescription(data));
            } else if (data.candidate) {
                await _pc.addIceCandidate(new RTCIceCandidate(data.candidate));
            } else if (data.type === 'bye') {
                // Remote peer ended the call — clear instantly
                clearRemoteVideo();
                pc.current?.close();
                pc.current = null;
            }
        } catch (err) {
            console.error("Signaling error:", err);
        }
    };

    const clearRemoteVideo = () => {
        // Instantly clear only the remote video
        if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
            remoteVideoRef.current.load();
        }
        setIsCallActive(false);
    };

    const destroyCall = () => {
        // Notify remote peer BEFORE tearing down
        sendSignaling({ type: 'bye' });

        // Stop local media
        localStream.current?.getTracks().forEach(t => t.stop());
        localStream.current = null;

        // Close peer connection
        pc.current?.close();
        pc.current = null;

        // Clear both video elements
        if (localVideoRef.current) {
            localVideoRef.current.srcObject = null;
            localVideoRef.current.load();
        }
        clearRemoteVideo();
    };

    const acquireMedia = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 1280, height: 720, facingMode: "user" },
                audio: true
            });
            localStream.current = stream;
            if (localVideoRef.current) localVideoRef.current.srcObject = stream;
            return stream;
        } catch (err) {
            console.error("Media error:", err);
            alert("Could not access camera/microphone.");
            return null;
        }
    };

    // --- Single, stable WebSocket effect (runs once) ---

    useEffect(() => {
        setStatus("Connecting...");
        const socket = new WebSocket(SIGNALING_SERVER_URL);
        ws.current = socket;

        socket.onopen = () => {
            setStatus("Connected");
            wsSend('JOIN_ROOM', { roomId: DEFAULT_ROOM_ID });
        };

        socket.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);

                switch (msg.type) {
                    case 'ROOM_STATE': {
                        const peers = msg.payload.userIds.filter(id => id !== msg.payload.currentUser);
                        if (peers.length > 0) {
                            targetUserIdRef.current = peers[0];
                            setPeerReady(true);
                        }
                        break;
                    }
                    case 'USER_JOINED':
                        // Only set target if no active peer (prevents hijacking mid-call)
                        if (!targetUserIdRef.current) {
                            targetUserIdRef.current = msg.payload.userId;
                            setPeerReady(true);
                        }
                        break;

                    case 'USER_LEFT':
                        if (msg.payload.userId === targetUserIdRef.current) {
                            targetUserIdRef.current = null;
                            setPeerReady(false);
                            clearRemoteVideo(); // Instant cleanup, keep local camera ready
                            // Also clean up peer connection
                            if (pc.current) {
                                pc.current.close();
                                pc.current = null;
                            }
                        }
                        break;

                    case 'SIGNALING_MESSAGE':
                        handleSignaling(msg.data);
                        break;

                    case 'ERROR':
                        console.error("Server error:", msg.payload.message);
                        setStatus("Error");
                        break;

                    default: break;
                }
            } catch (e) {
                console.error("Parse error:", e);
            }
        };

        socket.onerror = () => setStatus("Error");
        socket.onclose = () => setStatus("Disconnected");

        return () => {
            destroyCall();
            socket.close();
        };
        // eslint-disable-next-line
    }, []); // Intentionally empty — all mutable state is in refs

    // --- User Actions ---

    const handleStartCall = async () => {
        if (!targetUserIdRef.current) return alert("No peer in room to call.");

        // Acquire media - abort if user denies camera/mic access
        const stream = await acquireMedia();
        if (!stream) return;

        const _pc = createPeerConnection();
        try {
            const offer = await _pc.createOffer();
            await _pc.setLocalDescription(offer);
            sendSignaling(offer);
        } catch (err) {
            console.error("Offer error:", err);
        }
    };

    // --- Render ---

    return (
        <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center px-4 md:px-8 py-8">
            <h1 className="text-2xl md:text-3xl font-bold mb-4">One2Talk Video Room</h1>

            <div className="flex flex-wrap gap-2 md:gap-4 mb-8 justify-center">
                <StatusBadge label="Socket" status={status} />
                <StatusBadge label="Room" status={DEFAULT_ROOM_ID} color="blue" />
                <StatusBadge
                    label="Peer"
                    status={peerReady ? "Ready" : "Waiting..."}
                    color={peerReady ? "green" : "yellow"}
                />
            </div>

            <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-8">
                <div className="bg-black rounded-lg overflow-hidden relative aspect-video shadow-lg border border-gray-700">
                    <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                    <div className="absolute bottom-2 left-2 bg-black/50 px-2 rounded text-sm">You</div>
                </div>

                <div className="bg-black rounded-lg overflow-hidden relative aspect-video shadow-lg border border-gray-700">
                    <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
                    <div className="absolute bottom-2 left-2 bg-black/50 px-2 rounded text-sm">Remote Peer</div>
                    {!isCallActive && peerReady && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <button
                                onClick={handleStartCall}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-full font-bold shadow-lg transition transform hover:scale-105"
                            >
                                Start Call
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {isCallActive && (
                <button
                    onClick={destroyCall}
                    className="px-8 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-bold shadow-lg transition"
                >
                    End Call
                </button>
            )}
        </div>
    );
};

const StatusBadge = ({ label, status, color = "green" }) => {
    const colors = {
        green: "bg-green-600",
        blue: "bg-blue-600",
        yellow: "bg-yellow-600",
        red: "bg-red-600"
    };
    const isError = status === "Error" || status === "Disconnected";
    const bg = isError ? colors.red : (colors[color] || colors.green);
    return (
        <div className={`px-3 md:px-4 py-1 rounded-full text-xs md:text-sm font-semibold flex items-center gap-1 md:gap-2 ${bg}`}>
            <span className="opacity-75">{label}:</span>
            <span>{status}</span>
        </div>
    );
};

export default VideoRoom;
